import {useEffect, useRef, useState} from 'react';
import {CircleStop, Radio, Send, Trash2, Wifi, WifiOff} from 'lucide-react';
import streamsByLocale from '../generated-futures-streams.json';
import type {Locale} from '../data/products';
import styles from './WebSocketStreamsPage.module.css';

type Scope = 'public' | 'private';
type Stream = {
  slug: string;
  title: string;
  description: string;
  server: string;
  subscriptionExample: unknown;
  acknowledgementExample: unknown;
  pushExample: unknown;
  requestFields: string[][];
  pushFields: string[][];
  documentationPath: string;
};
type Message = {id: number; receivedAt: string; content: string; kind: 'received' | 'sent' | 'system'};
type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

const streams = streamsByLocale as unknown as Record<Locale, Record<Scope, Stream[]>>;

function pretty(value: unknown) {
  return JSON.stringify(value, null, 2) ?? String(value);
}

function FieldsTable({title, rows, locale}: {title: string; rows: string[][]; locale: Locale}) {
  if (!rows.length) return null;
  return (
    <details className={styles.fields}>
      <summary>{title}</summary>
      <div className={styles.tableWrap}>
        <table>
          <thead>
            <tr>
              <th>{locale === 'zh-CN' ? '字段' : 'Field'}</th>
              <th>{locale === 'zh-CN' ? '类型' : 'Type'}</th>
              <th>{locale === 'zh-CN' ? '说明' : 'Description'}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row[0]}-${index}`}>
                <td><code>{row[0]}</code></td>
                <td>{row[1]}</td>
                <td>{row.at(-1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

function useWebSocket(stream: Stream, locale: Locale) {
  const zh = locale === 'zh-CN';
  const socketRef = useRef<WebSocket | null>(null);
  const messageIdRef = useRef(0);
  const [status, setStatus] = useState<ConnectionState>('disconnected');
  const [messages, setMessages] = useState<Message[]>([]);
  const [payload, setPayload] = useState(pretty(stream.subscriptionExample));
  const [error, setError] = useState('');

  const appendMessage = (content: string, kind: Message['kind']) => {
    setMessages((current) => [
      ...current.slice(-99),
      {id: ++messageIdRef.current, receivedAt: new Date().toLocaleTimeString(), content, kind},
    ]);
  };

  const disconnect = () => {
    socketRef.current?.close(1000, 'Disconnected by user');
    socketRef.current = null;
    setStatus('disconnected');
  };

  const connect = () => {
    if (socketRef.current && socketRef.current.readyState < WebSocket.CLOSING) {
      disconnect();
      return;
    }
    setError('');
    setStatus('connecting');
    try {
      const socket = new WebSocket(stream.server);
      socketRef.current = socket;
      socket.onopen = () => setStatus('connected');
      socket.onmessage = (event) => {
        const content = typeof event.data === 'string' ? event.data : String(event.data);
        try {
          const parsed = JSON.parse(content);
          appendMessage(pretty(parsed), 'received');
        } catch {
          appendMessage(content, 'received');
        }
      };
      socket.onerror = () => {
        setStatus('error');
        setError(zh ? '连接失败。请检查网络或稍后重试。' : 'Connection failed. Check your network and try again.');
      };
      socket.onclose = (event) => {
        if (socketRef.current === socket) socketRef.current = null;
        setStatus('disconnected');
        if (event.code !== 1000 && event.reason) appendMessage(`${event.code}: ${event.reason}`, 'system');
      };
    } catch (cause) {
      setStatus('error');
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  };

  const send = () => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    try {
      const parsed = JSON.parse(payload);
      const content = JSON.stringify(parsed);
      socket.send(content);
      appendMessage(pretty(parsed), 'sent');
      setError('');
    } catch (cause) {
      setError(zh ? `订阅消息必须是有效 JSON：${cause instanceof Error ? cause.message : ''}` : `Subscription message must be valid JSON: ${cause instanceof Error ? cause.message : ''}`);
    }
  };

  useEffect(() => () => {
    socketRef.current?.close(1000, 'Page closed');
  }, []);

  return {status, messages, payload, setPayload, error, connect, send, disconnect, clearMessages: () => setMessages([])};
}

export function WebSocketStreamPlayground({locale, scope, slug}: {locale: Locale; scope: Scope; slug: string}) {
  const stream = streams[locale][scope].find((channel) => channel.slug === slug);
  if (!stream || scope !== 'public') return null;
  return <PublicStreamPlayground stream={stream} locale={locale} />;
}

function PublicStreamPlayground({stream, locale}: {stream: Stream; locale: Locale}) {
  const zh = locale === 'zh-CN';
  const playground = useWebSocket(stream, locale);
  const connected = playground.status === 'connected';

  return (
    <div className={styles.playground}>
      <div className={styles.playgroundBar}>
        <span className={styles.connectionStatus} data-status={playground.status} role="status">
          {connected ? <Wifi size={16} /> : <WifiOff size={16} />}
          {connected ? (zh ? '已连接' : 'Connected') : playground.status === 'connecting' ? (zh ? '连接中' : 'Connecting') : (zh ? '未连接' : 'Disconnected')}
        </span>
        <button className={styles.actionButton} type="button" onClick={playground.connect} disabled={playground.status === 'connecting'}>
          {connected ? <CircleStop size={16} /> : <Wifi size={16} />}
          {connected ? (zh ? '断开' : 'Disconnect') : (zh ? '连接' : 'Connect')}
        </button>
      </div>
      {playground.error && <p className={styles.error} role="alert">{playground.error}</p>}
      {connected && <>
        <label className={styles.label} htmlFor={`${slugFrom(stream)}-payload`}>
          {zh ? '订阅消息（请求参数）' : 'Subscription message (request parameters)'}
        </label>
        <textarea
          id={`${slugFrom(stream)}-payload`}
          className={styles.payload}
          value={playground.payload}
          onChange={(event) => playground.setPayload(event.target.value)}
          spellCheck={false}
          rows={Math.min(12, Math.max(5, playground.payload.split('\n').length))}
        />
        <div className={styles.messageActions}>
          <button className={styles.actionButton} type="button" onClick={playground.send}>
            <Send size={15} />
            {zh ? '发送消息' : 'Send message'}
          </button>
        </div>
        <div className={styles.receiveHeader}>
          <h3>{zh ? '连接消息' : 'Connection messages'}</h3>
          <button className={styles.iconButton} type="button" onClick={playground.clearMessages} title={zh ? '清空消息' : 'Clear messages'} aria-label={zh ? '清空消息' : 'Clear messages'}>
            <Trash2 size={16} />
          </button>
        </div>
        <div className={styles.messageLog} aria-live="polite">
          {playground.messages.length ? playground.messages.map((message) => (
            <div className={styles.message} data-kind={message.kind} key={message.id}>
              <div><span>{message.kind === 'received' ? (zh ? '收到' : 'Received') : message.kind === 'sent' ? (zh ? '发送' : 'Sent') : 'System'}</span><time>{message.receivedAt}</time></div>
              <pre>{message.content}</pre>
            </div>
          )) : <p className={styles.emptyLog}>{zh ? '连接后，服务端消息会显示在这里。' : 'Server messages will appear here after connecting.'}</p>}
        </div>
      </>}
    </div>
  );
}

function slugFrom(stream: Stream) {
  return `futures-${stream.slug}`;
}

function StreamSection({stream, locale, scope}: {stream: Stream; locale: Locale; scope: Scope}) {
  const zh = locale === 'zh-CN';
  const isPublic = scope === 'public';
  const playground = useWebSocket(stream, locale);
  const statusText = {
    disconnected: zh ? '未连接' : 'Disconnected',
    connecting: zh ? '连接中' : 'Connecting',
    connected: zh ? '已连接' : 'Connected',
    error: zh ? '连接错误' : 'Connection error',
  }[playground.status];
  const StatusIcon = playground.status === 'connected' ? Wifi : playground.status === 'connecting' ? Radio : WifiOff;

  return (
    <section className={styles.stream} id={stream.slug}>
      <div className={styles.streamHeading}>
        <div>
          <h2>{stream.title}</h2>
          <p>{stream.description}</p>
        </div>
      </div>

      <div className={styles.endpoint}>
        <span>{zh ? '连接地址' : 'Connection URL'}</span>
        <code>{stream.server}</code>
      </div>

      {isPublic && (
        <div className={styles.playground}>
          <div className={styles.playgroundBar}>
            <div className={styles.connectionStatus} data-status={playground.status} role="status">
              <StatusIcon size={16} aria-hidden="true" />
              <span>{statusText}</span>
            </div>
            <button className={styles.actionButton} type="button" onClick={playground.connect} disabled={playground.status === 'connecting'}>
              {playground.status === 'connected' ? <CircleStop size={16} /> : <Wifi size={16} />}
              {playground.status === 'connected' ? (zh ? '断开' : 'Disconnect') : (zh ? '连接' : 'Connect')}
            </button>
          </div>

          <label className={styles.label} htmlFor={`${stream.slug}-payload`}>
            {zh ? '订阅消息' : 'Subscription message'}
          </label>
          <textarea
            id={`${stream.slug}-payload`}
            className={styles.payload}
            value={playground.payload}
            onChange={(event) => playground.setPayload(event.target.value)}
            spellCheck={false}
            rows={Math.min(12, Math.max(5, playground.payload.split('\n').length))}
          />
          <div className={styles.messageActions}>
            <button className={styles.actionButton} type="button" onClick={playground.send} disabled={playground.status !== 'connected'}>
              <Send size={15} />
              {zh ? '发送消息' : 'Send message'}
            </button>
            <span className={styles.hint}>
              {zh ? '连接成功后发送订阅消息' : 'Connect first, then send the subscription'}
            </span>
          </div>

          {playground.error && <p className={styles.error} role="alert">{playground.error}</p>}

          <div className={styles.receiveHeader}>
            <h3>{zh ? '连接消息' : 'Connection messages'}</h3>
            <button className={styles.iconButton} type="button" onClick={playground.clearMessages} title={zh ? '清空消息' : 'Clear messages'} aria-label={zh ? '清空消息' : 'Clear messages'}>
              <Trash2 size={16} />
            </button>
          </div>
          <div className={styles.messageLog} aria-live="polite">
            {playground.messages.length ? playground.messages.map((message) => (
              <div className={styles.message} data-kind={message.kind} key={message.id}>
                <div><span>{message.kind === 'received' ? (zh ? '收到' : 'Received') : message.kind === 'sent' ? (zh ? '发送' : 'Sent') : 'System'}</span><time>{message.receivedAt}</time></div>
                <pre>{message.content}</pre>
              </div>
            )) : <p className={styles.emptyLog}>{zh ? '连接后，服务端消息会显示在这里。' : 'Server messages will appear here after connecting.'}</p>}
          </div>
        </div>
      )}

      <div className={styles.examples}>
        <div className={styles.example}>
          <h3>{zh ? '订阅请求示例' : 'Subscription request example'}</h3>
          <pre><code>{pretty(stream.subscriptionExample)}</code></pre>
        </div>
        <div className={styles.example}>
          <h3>{zh ? '订阅回执示例' : 'Acknowledgement example'}</h3>
          <pre><code>{pretty(stream.acknowledgementExample)}</code></pre>
        </div>
        <div className={styles.example}>
          <h3>{zh ? '推送示例' : 'Push example'}</h3>
          <pre><code>{pretty(stream.pushExample)}</code></pre>
        </div>
      </div>

      <FieldsTable title={zh ? '订阅请求字段' : 'Subscription request fields'} rows={stream.requestFields} locale={locale} />
      <FieldsTable title={zh ? '推送字段' : 'Push fields'} rows={stream.pushFields} locale={locale} />
    </section>
  );
}

export default function WebSocketStreamsPanel({locale = 'en', scope = 'public'}: {locale?: Locale; scope?: Scope}) {
  const channels = streams[locale][scope];
  useEffect(() => {
    const updateHash = () => {
      let hash = window.location.hash.slice(1);
      try { hash = decodeURIComponent(hash); } catch {}
      if (hash) {
        const match = channels.find((channel) => channel.slug === hash.toLowerCase() || channel.title.toLowerCase() === hash.toLowerCase());
        if (match && match.slug !== hash.toLowerCase()) {
          window.history.replaceState(null, '', `${window.location.pathname}#${match.slug}`);
        }
        if (match) document.getElementById(match.slug)?.scrollIntoView();
      }
    };
    updateHash();
    window.addEventListener('hashchange', updateHash);
    return () => window.removeEventListener('hashchange', updateHash);
  }, [channels]);

  return (
    <div className={styles.page + ' weex-ws-streams-page'}>
      <div className={styles.channelList}>
        {channels.map((channel) => (
          <StreamSection key={channel.slug} stream={channel} locale={locale} scope={scope} />
        ))}
      </div>
    </div>
  );
}
